import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FiDownload, FiPenTool, FiPlay, FiPlus, FiTool } from 'react-icons/fi';

import Pagination from 'components/Pagination';
import Button from 'components/Button';
import NotFound from 'components/NotFound';
import Loader from 'components/ContentLoader/GenericLoader';
import Avatar from 'components/Avatar';

import NoSolicitacaoChamada from 'assets/Fill out-pana.svg';
import api from 'services/api';
import AppError from 'utils/AppError';
import { Criativo as CriativoItem, CriativoJobStatus } from 'pages/@types';

import { Table, Td } from 'styles/global';
import {
  Acoes,
  Container,
  TableCard,
  TableHeader,
  TableTitle,
  TableNameCell,
  TableDateCell,
  TableSolicitanteCell,
  TableActionsCell,
  TableScroll,
  OptionCard,
  FormGroups,
} from './styles';
import { format, parseISO } from 'date-fns';
import Flag from 'components/Flag';

import { downloadFile } from 'utils/index';
import config from 'config/environment';
import CriativoStore from './store';
import ProgressBar from '@ramonak/react-progress-bar';
import { useModal } from 'hooks/modal';
import { StatusCriativo } from 'utils/variables';
import Field from 'components/Field';
import { useDispatch } from 'react-redux';
import { show } from 'store/modules/dialog/actions';
import PlayerBaixa from 'components/PlayerBaixa';

type TipoFormularioCriativo = 'normal' | 'site';

export default function Criativo() {
  const [criativos, setCriativos] = useState<CriativoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const [loadingDownload, setLoadingDownload] = useState(false);

  const [showCriativoModal, setShowCriativoModal] = useState(false);
  const [tipoFormulario, setTipoFormulario] = useState<TipoFormularioCriativo>(
    'normal',
  );
  const idsPendentesRef = useRef<number[]>([]);
  const selectedId = useRef<number>(0);
  const { showModal, hideModal } = useModal();
  const dispatch = useDispatch();
  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });
  // Load de criativos
  useEffect(() => {
    const controller = new AbortController();
    async function loadCriativos() {
      setLoading(true);
      try {
        const response = await api.get('/criativos', {
          signal: controller.signal,
          params: { page },
        });
        if (response?.data) {
          const listaCriativos: CriativoItem[] = response.data.criativos.map(
            (criativo: CriativoItem) => ({
              ...criativo,
              is_favorito: Boolean(criativo.is_favorito),
            }),
          );

          setPages(response.data.pages);
          setCriativos(listaCriativos);

          idsPendentesRef.current = listaCriativos
            .filter(c => c.status === StatusCriativo.PENDENTE)
            .map(c => c.id);
        } else {
          toast.error('Falha de comunicação com o servidor');
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        if (e instanceof AppError || e instanceof Error) {
          toast.error(e.message);
        } else {
          toast.error('Erro inesperado');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }
    loadCriativos();
    return () => {
      controller.abort();
    };
  }, [page]);

  const handlePlayer = useCallback(
    (download_url?: string) => {
      if (download_url) {
        setPlayer({
          open: !player.open,
          midia_url: download_url,
          pipe: false,
        });
      } else {
        dispatch(
          show({
            type: 'warning',
            text: 'Criativo Indisponível',
            showConfirmButton: false,
          }),
        );
      }
    },
    [dispatch, player.open],
  );

  const closeVideo = useCallback(() => {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }, [player.open]);

  const favoritarCriativo = async (criativoId: number, isFavorito: boolean) => {
    try {
      if (isFavorito) {
        await api.delete(`/criativos/${criativoId}/favorito`);
      } else {
        await api.post(`/criativos/${criativoId}/favorito`);
      }
      setCriativos(prev =>
        prev.map(criativo =>
          criativo.id === criativoId
            ? { ...criativo, is_favorito: !isFavorito }
            : criativo,
        ),
      );
    } catch (error) {
      toast.error('Erro ao atualizar favorito');
    }
  };

  // Atualizando status
  const atualizarStatus = useCallback(async () => {
    const ids = idsPendentesRef.current;
    if (!ids.length) {
      console.log('não tem nenhum criativo pendente');
      return;
    }
    try {
      const response = await api.post('/criativos/status', { ids });
      if (response?.data) {
        const criativosEmAndamento: CriativoJobStatus[] = response.data;
        idsPendentesRef.current = criativosEmAndamento
          .filter(
            r =>
              !r.status || r.status.criativo_status === StatusCriativo.PENDENTE,
          )
          .map(r => r.id);
        setCriativos(prev =>
          prev.map(criativo => {
            const resultado = criativosEmAndamento.find(
              item => item.id === criativo.id,
            );
            if (!resultado?.status?.criativo_status) {
              return criativo;
            }

            return {
              ...criativo,
              status: resultado.status.criativo_status,
              porcentagem: resultado.status.percent,
              phase: resultado.status.phase,
              queue_position: resultado.status.queue_position,
            };
          }),
        );
      } else {
        console.log('Falha ao ler status');
      }
    } catch (error) {
      console.error('Erro ao consultar status dos criativos:', error);
    }
  }, [setCriativos]);

  // Atualização da tela a cada 5 segundos
  useEffect(() => {
    if (showCriativoModal) return;

    // Consulta imediatamente ao entrar/recarregar a página
    atualizarStatus();
    // Continua atualizando a cada 5 segundos
    const interval = setInterval(atualizarStatus, 5000);
    return () => clearInterval(interval);
  }, [atualizarStatus, showCriativoModal]);

  const abrirFormulario = useCallback(
    (tipo: TipoFormularioCriativo) => {
      hideModal();
      selectedId.current = 0;
      setTipoFormulario(tipo);
      setShowCriativoModal(true);
    },
    [hideModal],
  );
  const renderNovo = useCallback(() => {
    return (
      <div>
        <FormGroups $columns="1fr 1fr">
          <OptionCard>
            <h5>
              A leitura do site de seu cliente acontece em média de 2 minutos,
              porém será necessário refina-lo para de fato mandar para geração.
            </h5>
          </OptionCard>
          <OptionCard>
            <h5>A geração de criativos acontece em média 5 minutos.</h5>
          </OptionCard>
          <Button color="success" onClick={() => abrirFormulario('site')}>
            USAR SITE
          </Button>
          <Button color="secondary" onClick={() => abrirFormulario('normal')}>
            FORMULÁRIO COMPLETO
          </Button>
        </FormGroups>
      </div>
    );
  }, [abrirFormulario]);

  const handleNovo = useCallback(() => {
    showModal({
      width: '35vw',
      title: 'COMO DESEJA COMEÇAR?',
      component: renderNovo(),
    });
  }, [renderNovo, showModal]);

  return (
    <Container>
      <TableCard>
        <TableHeader>
          <TableTitle>LISTA DE CRIATIVOS</TableTitle>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <Button
            color="primary"
            onClick={() => {
              selectedId.current = 0;
              handleNovo();
            }}
            tooltip="Gerar novo Criativo"
          >
            GERAR NOVO CRIATIVO
            <FiPlus />
          </Button>
        </TableHeader>
        {loading ? (
          <Loader />
        ) : !!criativos && criativos.length ? (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>NOME DO CRIATIVO</th>
                  <th>STATUS DO CRIATIVO</th>
                  <th>CRIADO EM</th>
                  <th>SOLICITANTE</th>
                  <th>AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {criativos.map(c => (
                  <tr key={c.id}>
                    <TableNameCell>{c.nomeproduto}</TableNameCell>
                    <Td>
                      {c.status === StatusCriativo.PENDENTE ? (
                        c.porcentagem === 2 || c.phase === 'preparing' ? (
                          <Flag name="Consultando..." type="pendente" />
                        ) : c.porcentagem != null && c.porcentagem > 0 ? (
                          <ProgressBar
                            completed={c.porcentagem}
                            height="22px"
                            width="150px"
                            bgColor="#4fbe79"
                            baseBgColor="#E5E5E5"
                            borderRadius="5px"
                          />
                        ) : c.phase === 'queued' || c.queue_position != null ? (
                          <Flag
                            name={
                              c.queue_position != null
                                ? `${c.queue_position} na fila`
                                : 'Consultando...'
                            }
                            type="pendente"
                          />
                        ) : (
                          <Flag name="Consultando..." type="pendente" />
                        )
                      ) : (
                        <Flag name={c.status} type={c.status} />
                      )}
                    </Td>
                    <TableDateCell>
                      {format(parseISO(c.created_at), 'dd/MM/yyyy HH:mm:ss')}
                    </TableDateCell>
                    <TableSolicitanteCell>
                      <Avatar
                        size="40"
                        name={c.solicitante?.nome}
                        src={c.solicitante?.avatar_url}
                        round
                      />
                    </TableSolicitanteCell>
                    <TableActionsCell>
                      <Acoes>
                        <Button
                          type="button"
                          fill
                          color="font"
                          round
                          onClick={() =>
                            favoritarCriativo(c.id, c.is_favorito ?? false)
                          }
                          aria-label={
                            c.is_favorito
                              ? 'Remover dos favoritos'
                              : 'Adicionar aos favoritos'
                          }
                          style={{ fontSize: '25px' }}
                        >
                          {c.is_favorito ? '★⁠' : '☆'}
                        </Button>
                        <Button
                          fill
                          color="font"
                          round
                          style={{ fontSize: '22px' }}
                          onClick={() => {
                            selectedId.current = c.id;
                            setTipoFormulario('normal');
                            setShowCriativoModal(true);
                          }}
                          tooltip="Refinar Resultado"
                          disabled={
                            loadingDownload ||
                            c.status === StatusCriativo.PENDENTE
                          }
                        >
                          <FiPenTool />
                        </Button>
                        <Button
                          fill
                          color="font"
                          round
                          type="button"
                          onClick={() => handlePlayer(c.download_url)}
                          tooltip="Baixar Criativo"
                          disabled={
                            loadingDownload ||
                            c.status === StatusCriativo.PENDENTE ||
                            !c.download_url
                          }
                        >
                          <FiPlay />
                        </Button>
                      </Acoes>
                    </TableActionsCell>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        ) : (
          <NotFound img={NoSolicitacaoChamada} title="Criativos">
            Nenhum Criativo encontrado
          </NotFound>
        )}
      </TableCard>
      {showCriativoModal && (
        <CriativoStore
          selectedId={selectedId.current}
          tipoFormulario={tipoFormulario}
          setShowCriativoModal={setShowCriativoModal}
          setCriativos={setCriativos}
          idsPendentesRef={idsPendentesRef}
        />
      )}
      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
      />
    </Container>
  );
}
